'use client'
import { useEffect, useState } from 'react'
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { extractTokenFromClientSession } from 'modules/customer'
import {
  avalibleForDeliveryDay,
  getProductSubstitutesById,
  ProductCard,
  ProductTypes
} from 'modules/products'
import { SeoListName } from 'shared/analytics'
import { distributionCenterId } from 'shared/config/distributionCenter'
import IconButton from 'shared/ui/IconButton'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { createAsPath } from 'shared/utils/searchParams'

import styles from './SubstitutesSlider.module.scss'

const REPLACEMENTS_LIST_NAME = SeoListName.REPLACEMENTS_LIST_NAME

interface SubstitutesSliderProps {
  product: ProductTypes
  categoryAgeLimitMet?: boolean
  dimensionsSlider: {
    width: number
    height: number
  }
  deliveryDayAsNumber: number | null
  deliveryMethodId?: string | null
}

const SubstitutesSlider = ({
  product,
  categoryAgeLimitMet,
  dimensionsSlider,
  deliveryDayAsNumber,
  deliveryMethodId
}: SubstitutesSliderProps) => {
  const [activeSliderPosition, setActiveSliderPosition] = useState<number>(0)
  const [disabledScrollRight, setDisabledScrollRight] = useState<boolean>(false)
  const { id } = product
  const [replacements, setReplacements] = useState<ProductTypes[]>([])

  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const [loading, setLoading] = useState(true)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const asPath = createAsPath(pathname, searchParams)

  useEffect(() => {
    const trigger = async () => {
      try {

        setLoading(true)
        const resp = await getProductSubstitutesById({
          token,
          productId: id,
          distributionCenterId
        })
        const substitutes = resp.data?.data

        const avalibleSubstitutes = substitutes.filter(substitute => {
          const availableByDay = avalibleForDeliveryDay(
            substitute,
            deliveryDayAsNumber
          )
          const availableByDeliveryMethod = deliveryMethodId
            ? substitute.deliveryMethods.some(
                method => method.id === deliveryMethodId
              )
            : true

          return availableByDay && availableByDeliveryMethod
        })

        setReplacements(avalibleSubstitutes)
      } catch (err) {
        toast.error(getApiErrors(err))
      } finally {
        setLoading(false)
      }
    }

    void trigger()
  }, [deliveryDayAsNumber, id, token, deliveryMethodId])

  const replacementWidth = 228
  const replacementsSum = replacements.length

  useEffect(() => {
    // @sieradz - blocking if the width of the dimensionsSlider is greater / equal to the number of sliders multiplied by their number
    setDisabledScrollRight(
      dimensionsSlider.width >= replacementWidth * replacementsSum
    )
  }, [dimensionsSlider.width, replacementsSum])

  useEffect(() => {
    const checkIfDisabledScrollRight = () => {
      // @sieradz - autorun if we should disable scroll to right
      if (
        (dimensionsSlider.width + Math.abs(activeSliderPosition)) /
          replacementWidth >=
        replacementsSum
      ) {
        setDisabledScrollRight(true)
      } else {
        setDisabledScrollRight(false)
      }
    }

    checkIfDisabledScrollRight()
  }, [activeSliderPosition, dimensionsSlider, replacementsSum])

  const scrollLeft = () => {
    setActiveSliderPosition(activeSliderPosition + replacementWidth + 8) // @sieradz - plus padding
  }

  const scrollRight = () => {
    setActiveSliderPosition(activeSliderPosition - replacementWidth - 8) // @sieradz - minus padding
  }

  const hasReplacements = replacementsSum > 0

  return (
    <div className={styles.wrapper}>
      {hasReplacements && (
        <div className={styles.controls}>
          <IconButton
            className={styles.iconButton}
            variant='gray'
            onClick={scrollLeft}
            size='medium'
            icon={<IconArrowLeft />}
            disabled={activeSliderPosition === 0}
            aria-label='Przejdź do poprzedniego zamiennika'
          />
          <IconButton
            className={styles.iconButton}
            variant='gray'
            onClick={scrollRight}
            size='medium'
            icon={<IconArrowRight />}
            disabled={disabledScrollRight}
            aria-label='Przejdź do następnego zamiennika'
          />
        </div>
      )}
      <hr className={styles.hr} />
      {loading ? (
        <div className={styles.loadingStateWrapper}>
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {hasReplacements ? (
            <>
              <div className={styles.title}>Zamienniki wybrane dla Ciebie</div>
              <div
                className={styles.sliderWrapperReplacements}
                style={
                  {
                    '--slider-width': `${dimensionsSlider.width - 100}px`,
                    '--slider-width-mobile': `${
                      Math.floor(dimensionsSlider.width / replacementWidth) *
                      (replacementWidth + 16)
                    }px`
                  } as React.CSSProperties
                }
              >
                <div className={styles.sliderContainerReplacements} id={id}>
                  <div
                    className={styles.sliderItemsReplacements}
                    style={
                      {
                        '--slider-position': `${activeSliderPosition}px`
                      } as React.CSSProperties
                    }
                  >
                    {replacements.map(
                      (replacement: ProductTypes, idx: number) => {
                        return (
                          <div
                            className={styles.productWrapperReplacements}
                            key={replacement.id}
                          >
                            <ProductCard
                              ageLimitMetCategory={categoryAgeLimitMet}
                              isSubstitute
                              realProductId={product.id} // real product
                              product={replacement} // replacement
                              productPath={replacement?.slug || ''}
                              seoButtonPlace={asPath}
                              listName={REPLACEMENTS_LIST_NAME}
                              idxOnList={idx}
                              id={replacement.id}
                            />
                          </div>
                        )
                      }
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.titleReplacements}>
              Brak proponowanych zamienników
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default SubstitutesSlider
